"use client";

import React, { useEffect, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { ChevronFirst, ChevronLast, ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import {
    Pagination,
    PaginationContent,
    PaginationItem,
    PaginationLink,
} from "@/components/ui/pagination";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

interface TablePaginationProps {
  currentPage: number;
  pageCount: number;
  onPageChange: (page: number) => void;
  className?: string;
  /** When set, syncs the current page to the URL query param. */
  pageParamName?: string;
  /** Total number of items across all pages. When provided, shows "Showing X to Y of Z" label. */
  totalItems?: number;
  /** Items per page. Required when totalItems is provided. */
  itemsPerPage?: number;
  /** Label for the items (default: "items"). */
  itemLabel?: string;
}

export const TablePagination: React.FC<TablePaginationProps> = ({
  currentPage,
  pageCount,
  onPageChange,
  className,
  pageParamName,
  totalItems,
  itemsPerPage,
  itemLabel = "items",
}) => {
    const router = useRouter();
    const searchParams = useSearchParams();

    // While the API is refetching (e.g. switching pages), pageCount can briefly
    // come back as 0/undefined — keep the last known good value so the page
    // selector doesn't go blank mid-load.
    const [stablePageCount, setStablePageCount] = useState(pageCount);
    useEffect(() => {
        if (pageCount > 0) setStablePageCount(pageCount);
    }, [pageCount]);
    // Also never let it drop below the current page — on a hard refresh, no
    // data has loaded yet at all, so there's no "last known good" value to
    // fall back to; the current page itself (already known from the URL)
    // must still be a valid, selectable option.
    const effectivePageCount = Math.max(pageCount, stablePageCount, currentPage, 1);

    const handlePageChange = (p: number) => {
        if (p < 1 || p > effectivePageCount || Number.isNaN(p)) return;
        onPageChange(p);
        if (pageParamName) {
            const params = new URLSearchParams(searchParams.toString());
            params.set(pageParamName, String(p));
            router.replace(`${window.location.pathname}?${params.toString()}`);
        }
    };

    const navLink = (
        disabled: boolean,
        label: string,
        icon: React.ReactNode,
        onClick: () => void,
    ) => (
        <PaginationLink
            href="#"
            aria-label={label}
            aria-disabled={disabled}
            size="icon"
            className={cn(
                "rounded-full border border-border bg-background text-muted-foreground transition-colors",
                "hover:border-iai-purple hover:bg-iai-purple-4 hover:text-iai-purple",
                disabled && "pointer-events-none opacity-40",
            )}
            onClick={(e) => {
                e.preventDefault();
                if (!disabled) onClick();
            }}
        >
            {icon}
        </PaginationLink>
    );

    const showingFrom = totalItems && itemsPerPage ? (currentPage - 1) * itemsPerPage + 1 : null;
    const showingTo   = totalItems && itemsPerPage ? Math.min(currentPage * itemsPerPage, totalItems) : null;

    return (
        <div
            className={cn(
                "flex flex-col gap-3 border-t border-border bg-card px-4 py-3.5 sm:flex-row sm:items-center sm:justify-between sm:px-5",
                className,
            )}
        >
            {showingFrom && showingTo && totalItems ? (
                <p className="text-[13px] text-muted-foreground">
                    Showing {showingFrom.toLocaleString()} to {showingTo.toLocaleString()} of{" "}
                    <span className="font-medium text-foreground">{totalItems.toLocaleString()}</span>{" "}
                    {itemLabel}
                </p>
            ) : (
                <span />
            )}

            <Pagination className="mx-0 w-auto justify-end">
                <PaginationContent className="gap-1.5">
                    <PaginationItem>
                        {navLink(currentPage <= 1, "Go to first page", <ChevronFirst className="size-4" />, () =>
                            handlePageChange(1),
                        )}
                    </PaginationItem>
                    <PaginationItem>
                        {navLink(currentPage <= 1, "Go to previous page", <ChevronLeft className="size-4" />, () =>
                            handlePageChange(currentPage - 1),
                        )}
                    </PaginationItem>

                    <PaginationItem>
                        <Select
                            value={String(currentPage)}
                            onValueChange={(value) => handlePageChange(Number(value))}
                        >
                            <SelectTrigger
                                aria-label="Select page"
                                className="h-9 w-fit gap-1.5 rounded-full border-border px-3 text-[13px] shadow-none"
                            >
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {Array.from({ length: effectivePageCount }, (_, i) => i + 1).map((page) => (
                                    <SelectItem key={page} value={String(page)}>
                                        Page {page}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </PaginationItem>

                    <PaginationItem>
                        {navLink(currentPage >= effectivePageCount, "Go to next page", <ChevronRight className="size-4" />, () =>
                            handlePageChange(currentPage + 1),
                        )}
                    </PaginationItem>
                    <PaginationItem>
                        {navLink(currentPage >= effectivePageCount, "Go to last page", <ChevronLast className="size-4" />, () =>
                            handlePageChange(effectivePageCount),
                        )}
                    </PaginationItem>
                </PaginationContent>
            </Pagination>
        </div>
    );
};

export default TablePagination;
